import { GroundTruthJobStatus, GroundTruthRunKind, GroundTruthRunSummary, GroundTruths } from "@app/shared";
import { useMemo, useState } from "react";
import { useFetch } from "../../../hooks";
import { PageContainer, Icons, LINK_BUTTON, SortableTable, SortableTableHeaderType } from "../../shared";
import { useDialog } from "@app/shared";
import { GroundTruthDebugDialog } from "../facility/GroundTruthDebugDialog";
import JsonViewer from "../../shared/JsonViewer";
import {
    countColumn, detailColumn, formatInZone, photosColumn, statusColumn, uploadedColumn,
} from "../facility/parkingView/groundTruthColumns";
import { describeClassCounts, parseClassCounts } from "../facility/groundTruthSummary";

/**
 * Every ground truth run across the fleet, uploads and anonymous trials together.
 *
 * The parking view answers "what has this facility counted lately"; this answers "what has this
 * feature been doing, and who is using it" — including the trial endpoint, which takes no
 * credentials and belongs to no customer, and whose rows are the only record of that traffic.
 *
 * Times are shown in UTC rather than a facility's zone. The list spans facilities in different
 * zones and includes rows that have no facility at all, so there is no single local zone to render
 * in; UTC is the one unambiguous answer, and it is labelled as such rather than left to guess.
 */
export const GroundTruthing: React.FC = () => {
    const [limit, setLimit] = useState(100);
    const [kindFilter, setKindFilter] = useState<"all" | "job" | "trial">("all");
    const [failedOnly, setFailedOnly] = useState(false);
    const [runs, error, helpers] = useFetch(() => GroundTruths.getRuns(limit), [limit]);
    const detailDialog = useDialog<{ id: string; kind: "job" | "trial" }>();
    const responseDialog = useDialog<GroundTruthRunSummary>();

    const visible = useMemo(() => (runs ?? []).filter(run =>
        (kindFilter === "all"
            || (kindFilter === "trial") === (run.kind === GroundTruthRunKind.Trial))
        && (!failedOnly || run.status === GroundTruthJobStatus.Failed)), [runs, kindFilter, failedOnly]);

    /**
     * Who has been calling the unauthenticated trial endpoint, busiest first.
     *
     * Only trials: an upload has an account behind it and a facility to bill it to, so "who" is
     * already answered. A trial has neither, and this is the only place the question gets asked.
     * Computed over the loaded window rather than queried, so it describes what is on screen —
     * widen the window and the picture widens with it.
     */
    const topCallers = useMemo(() => {
        const byAddress = new Map<string, { runs: number; bytes: number }>();
        for (const run of runs ?? []) {
            if (run.kind !== GroundTruthRunKind.Trial || !run.clientIp) continue;
            const entry = byAddress.get(run.clientIp) ?? { runs: 0, bytes: 0 };
            entry.runs += 1;
            entry.bytes += run.totalBytes ?? 0;
            byAddress.set(run.clientIp, entry);
        }
        return [...byAddress.entries()]
            .sort((a, b) => b[1].runs - a[1].runs)
            .slice(0, 5);
    }, [runs]);

    const columns = useMemo((): SortableTableHeaderType<GroundTruthRunSummary>[] => [
        uploadedColumn<GroundTruthRunSummary>("UTC", "UTC"),
        {
            dataKey: "kind", header: "Source", width: 80,
            renderFunc: (run) => run?.kind === GroundTruthRunKind.Trial
                // Named for what it is from the outside: an anonymous demo, not a customer's count.
                ? <span className="text-semi-bright-text">Trial</span>
                : "Upload",
        },
        {
            dataKey: "facilityName", header: "Facility", width: 180,
            // A trial names no facility. An em dash rather than blank, so an empty cell reads as
            // "there is no facility" instead of "the name failed to load".
            renderFunc: (run) => run?.facilityName ?? "—",
        },
        {
            dataKey: "requestedBy", header: "User", width: 180,
            renderFunc: (run) => {
                if (run?.requestedBy) return run.requestedBy;
                // What an anonymous run has instead of a user, in the column asking who did this.
                if (run?.clientIp) return <span className="text-semi-bright-text">{run.clientIp}</span>;
                return "—";
            },
        },
        photosColumn<GroundTruthRunSummary>(),
        statusColumn<GroundTruthRunSummary>(),
        countColumn<GroundTruthRunSummary>(),
        {
            dataKey: "counterModel", header: "Model", width: 160,
            // The model, not the counter name: two runs through the same counter on different
            // models are different measurements, and the model is what says which.
            renderFunc: (run) => run?.counterModel
                ?? <span className="text-semi-bright-text">—</span>,
        },
        {
            dataKey: "classCountsJson", header: "Breakdown", width: 280,
            renderFunc: (run) => {
                if (run?.errorMessage) return <span className="text-red-600">{run.errorMessage}</span>;
                const counts = parseClassCounts(run?.classCountsJson);
                return Object.keys(counts).length ? describeClassCounts(counts) : "—";
            },
        },
        {
            dataKey: "inferenceMs", header: "Time", width: 90,
            // Stitch plus inference, which is what the run cost us in compute. Not end to end:
            // queue time on the async paths says more about workers than about the counter.
            renderFunc: (run) => {
                const ms = (run?.stitchMs ?? 0) + (run?.inferenceMs ?? 0);
                return ms > 0 ? `${(ms / 1000).toFixed(1)}s` : "—";
            },
        },
        {
            dataKey: "totalBytes", header: "Size", width: 80,
            // Recorded for trials only, and shown because it is a cost signal on an open endpoint.
            renderFunc: (run) => run?.totalBytes
                ? `${(run.totalBytes / (1024 * 1024)).toFixed(1)} MB`
                : "—",
        },
        // Both kinds, each through its own route. Trials are where the stitcher meets the
        // messiest batches, so they are the rows most worth being able to open.
        detailColumn<GroundTruthRunSummary>(
            run => run.id,
            id => {
                const run = (runs ?? []).find(r => r.id === id);
                detailDialog.show("Ground Truth Detail", "Close",
                    { id: id, kind: run?.kind === GroundTruthRunKind.Trial ? "trial" : "job" },
                    () => { });
            }),
        {
            header: "", width: 110, sortable: false,
            // What the caller actually received, verbatim. The whole reason this is stored is that
            // reconstructing it afterwards is guesswork — so it is one click, not a reasoning step.
            renderFunc: (run) => run?.responseJson
                ? <button
                    type="button"
                    className={`text-link-page hover:text-link-page-hover underline whitespace-nowrap ${LINK_BUTTON}`}
                    onClick={() => responseDialog.show(
                        `Response · HTTP ${run.responseStatusCode ?? "?"}`, "Close", run, () => { })}
                >
                    View response
                </button>
                : null,
        },
    ], [detailDialog, responseDialog, runs]);

    return (
        <>
            <PageContainer
                title="Ground Truthing"
                Icon={Icons.Photo}
                titleRight={
                    <div className="flex items-center gap-3 text-sm">
                        <span className="text-semi-bright-text">
                            Times in UTC{runs ? ` · ${visible.length} of ${runs.length}` : ""}
                        </span>
                        <select
                            className="border border-separators-page rounded px-2 py-1 bg-background-page"
                            value={kindFilter}
                            onChange={e => setKindFilter(e.target.value as "all" | "job" | "trial")}
                        >
                            <option value="all">All sources</option>
                            <option value="job">Uploads</option>
                            <option value="trial">Trials</option>
                        </select>
                        <label className="flex items-center gap-1">
                            <input type="checkbox" checked={failedOnly}
                                onChange={e => setFailedOnly(e.target.checked)} />
                            Failed only
                        </label>
                        <select
                            className="border border-separators-page rounded px-2 py-1 bg-background-page"
                            value={limit}
                            onChange={e => setLimit(Number(e.target.value))}
                        >
                            {[50, 100, 250, 500].map(n => <option key={n} value={n}>Last {n}</option>)}
                        </select>
                    </div>
                }
            >
                {topCallers.length > 0 &&
                    // Above the table rather than in it: this is the question the trial endpoint
                    // raises — it takes no credentials and spends a paid model call per request —
                    // and it is not answerable by sorting rows.
                    <div className="mb-3 text-sm">
                        <span className="text-semi-bright-text">Busiest trial callers in this window: </span>
                        {topCallers.map(([ip, stat], i) => (
                            <span key={ip}>
                                {i > 0 && " · "}
                                <span className="tabular-nums">{ip}</span>
                                <span className="text-semi-bright-text">
                                    {" "}({stat.runs} {stat.runs === 1 ? "run" : "runs"}
                                    {stat.bytes > 0 && `, ${(stat.bytes / (1024 * 1024)).toFixed(0)} MB`})
                                </span>
                            </span>
                        ))}
                    </div>}

                {error
                    ? <p className="text-sm text-red-600">Could not load ground truth runs.</p>
                    : <SortableTable
                        data={visible}
                        columns={columns}
                        initialSortKey="requestDate"
                        initialSortDir="desc"
                        isLoading={helpers.isLoading}
                        emptyValue="No ground truth runs yet."
                    />}
            </PageContainer>

            {responseDialog.renderDialog(
                (run) => !run?.responseJson ? <></> : (
                    <div className="max-h-[60vh] overflow-auto space-y-2">
                        <p className="text-xs text-semi-bright-text">
                            Recorded when the response was sent — not rebuilt from the row now.
                        </p>
                        {/* Parsed here rather than in the column, so a row whose stored response is
                            not valid JSON shows the raw text instead of taking the tab down. */}
                        <StoredResponse json={run.responseJson} />
                    </div>
                ),
                { closeOnOutsideClick: true, className: "w-[75%] overflow-hidden", showCancelButton: false })}

            {/* renderDialog calls this on every render, including before anything is selected, so
                the value is genuinely undefined most of the time — it is not a case to assert away.
                Rendering the detail with no id would also fire its fetch against undefined. */}
            {detailDialog.renderDialog(
                (target) => target
                    ? <GroundTruthDebugDialog jobId={target.id} kind={target.kind} />
                    : <></>,
                { closeOnOutsideClick: true, className: "w-full max-w-6xl", showCancelButton: false })}
        </>
    );
};

type StoredResponseProps = { json: string };

/**
 * The recorded response, rendered as JSON when it parses and as text when it does not.
 *
 * JSON.parse inside a render callback throws straight through React and takes the whole tab with
 * it — which is exactly the failure this tab exists to help diagnose, so it must not be the one it
 * causes. A blob that will not parse is still the most informative thing we have about that run.
 */
const StoredResponse: React.FC<StoredResponseProps> = ({ json }) => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return <pre className="text-xs whitespace-pre-wrap break-all">{json}</pre>;
    }
    return <JsonViewer data={parsed} />;
};
